import { BMInputField } from "@traveloka/district-bloom";
import IcSystemChevronDown from "@traveloka/icon-kit-web/react/IcSystemChevronDown";
import { useCallback, useMemo, useRef, useState } from "react";
import { Dimensions, Platform, View } from "react-native";

import { useMobileContentResource } from "../../core/resource/MobileResourceProvider";
import { appendTestId, formatMessage } from "../../utils";
import BMView from "../View/BMView";
import BMDropdownDesktopOptions from "./components/BMDropdownDesktopOptions";
import BMDropdownMobileOptions from "./components/BMDropdownMobileOptions";
import { useGetValue } from "./hooks";
import {
  BMDropdownFieldMultipleProps,
  BMDropdownFieldProps,
  BMDropdownOptionsValue,
} from "./types";

const IS_MOBILE = Dimensions.get("window").width < 768;

type FieldOnChange<T> = ((value: T | null) => void) | ((value: T[]) => void);

/**
 * `onChange`'s real shape is tied to `variant`, which the type system can't
 * correlate automatically once destructured from the props union. These
 * predicates encode that invariant explicitly instead of casting.
 */
function isSingleOnChange<T>(
  isMultiple: boolean,
  fn: FieldOnChange<T> | undefined
): fn is ((value: T | null) => void) | undefined {
  return !isMultiple;
}

function isMultipleOnChange<T>(
  isMultiple: boolean,
  fn: FieldOnChange<T> | undefined
): fn is ((value: T[]) => void) | undefined {
  return isMultiple;
}

function isMultipleFieldProps<T>(
  props: BMDropdownFieldProps<T>
): props is BMDropdownFieldMultipleProps<T> {
  return props.variant === "MULTIPLE";
}

export default function BMDropdownField<T = string>(
  props: BMDropdownFieldProps<T>
) {
  const {
    value,
    label = "",
    option,
    zIndex = 9,
    isRequired = false,
    variant = "DEFAULT",
    searchable,
    placeholder,
    optionsSection,
    onChange,
    getOptionValue,
    getOptionLabel,
    renderSelectionItem,
    isSelectedOnTop = true,
    testID,
    error,
    ...rest
  } = props;

  const isMultiple = isMultipleFieldProps(props);
  const { formatDisplayValue } = isMultiple
    ? props
    : { formatDisplayValue: undefined };

  const [isOptionVisible, setIsOptionVisible] = useState(false);
  const [query, setQuery] = useState("");
  const wrapperRef = useRef<View>(null);

  const getValue = useGetValue(getOptionValue);

  /** Array when isMultiple, 0-or-1 items otherwise, so every downstream check below is unconditional. */
  const selectedItems = useMemo((): BMDropdownOptionsValue<T>[] => {
    if (isMultiple) {
      if (!Array.isArray(value)) return [];
      const selectedValues = value.map((v) => getValue(v));
      return option.items.filter((item) =>
        selectedValues.includes(getValue(item.value))
      );
    }
    if (Array.isArray(value) || value == null) return [];
    const selectedValue = getValue(value);
    const found = option.items.find(
      (item) => getValue(item.value) === selectedValue
    );
    return found ? [found] : [];
  }, [isMultiple, option.items, value, getValue]);

  const optionItems = useMemo((): BMDropdownOptionsValue<T>[] => {
    if (!option.items) return [];
    if (isMultiple || !isSelectedOnTop || selectedItems.length === 0) {
      return option.items;
    }
    const [selectedItem] = selectedItems;
    const selectedValue = getValue(selectedItem.value);
    return [
      selectedItem,
      ...option.items.filter((item) => getValue(item.value) !== selectedValue),
    ];
  }, [isMultiple, selectedItems, isSelectedOnTop, option.items, getValue]);

  /** Single-select (both platforms) and mobile MULTIPLE's Confirm: commits and closes. */
  const handleOptionChange = useCallback(
    (item: BMDropdownOptionsValue<T> | BMDropdownOptionsValue<T>[] | null) => {
      setIsOptionVisible(false);
      if (Array.isArray(item)) {
        if (isMultipleOnChange(isMultiple, onChange)) {
          onChange?.(item.map((item) => item.value));
        }
      } else if (isSingleOnChange(isMultiple, onChange)) {
        onChange?.(item?.value ?? null);
      }
      setQuery("");
    },
    [isMultiple, onChange]
  );

  /** Desktop MULTIPLE: commits every toggle immediately, keeps the panel open. */
  const handleDesktopMultipleChange = useCallback(
    (items: BMDropdownOptionsValue<T> | BMDropdownOptionsValue<T>[] | null) => {
      if (isMultipleOnChange(isMultiple, onChange) && Array.isArray(items)) {
        onChange?.(items.map((item) => item.value));
      }
    },
    [isMultiple, onChange]
  );

  function toggleOptionVisible() {
    setIsOptionVisible((prev) => {
      if (!prev) setQuery(searchable ? selectedLabel : "");
      return !prev;
    });
  }

  const { CorporateCommon: cr } = useMobileContentResource();

  const selectedLabel = useMemo(() => {
    if (formatDisplayValue) return formatDisplayValue(selectedItems);
    if (selectedItems.length === 0) return "";
    if (selectedItems.length === 1) {
      const [item] = selectedItems;
      return getOptionLabel ? getOptionLabel(item) : item.label;
    }
    return formatMessage(cr.numSelected, { num: selectedItems.length });
  }, [selectedItems, getOptionLabel, formatDisplayValue, cr.numSelected]);

  const showDesktopOptions = Platform.OS === "web" && !IS_MOBILE;
  const showMobileOptions = Platform.OS !== "web" || IS_MOBILE;

  const optionsProps = {
    selected: selectedItems,
    show: isOptionVisible,
    isRadio: variant === "RADIO",
    isMultiple,
    option: {
      ...option,
      items: optionItems,
    },
    searchable,
    getOptionValue,
    renderSelectionItem,
    onChange: handleOptionChange,
    error,
  };

  return (
    <BMView style={{ zIndex }} ref={wrapperRef}>
      <BMInputField
        value={isOptionVisible && searchable ? query : selectedLabel}
        label={`${label}${isRequired ? " *" : ""}`}
        variant={showMobileOptions || !searchable ? "DROPDOWN" : "FIELD"}
        buttonInsetType="BUTTON_ICON"
        buttonInsetProps={{
          Icon: IcSystemChevronDown,
          onPress: toggleOptionVisible,
        }}
        error={!!error}
        placeholder={placeholder}
        testID={appendTestId(testID, "input")}
        {...(showMobileOptions || !searchable
          ? { onDropdownToggle: toggleOptionVisible }
          : {
              onFocus: () => setIsOptionVisible(true),
              onChange: (e: any) => setQuery(e.nativeEvent.text),
            })}
        {...rest}
      />
      {showDesktopOptions && (
        <BMDropdownDesktopOptions
          {...optionsProps}
          onChange={
            isMultiple ? handleDesktopMultipleChange : handleOptionChange
          }
          onClose={() => setIsOptionVisible(false)}
          query={query}
          wrapperRef={wrapperRef}
          optionsSection={optionsSection}
        />
      )}
      {showMobileOptions && (
        <BMDropdownMobileOptions
          {...optionsProps}
          optionsSection={optionsSection}
          onClose={() => setIsOptionVisible(false)}
        />
      )}
    </BMView>
  );
}
